import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  FileText,
  FilePdf,
  FileImage,
  FileDoc,
  FileXls,
  FileZip,
  Upload,
  Download,
  Eye,
  Trash,
  FolderOpen,
  CaretDown,
  CaretUp,
  Shield,
  Certificate,
  Blueprint,
  Receipt,
  Camera,
  Warning as WarningIcon,
  CheckCircle,
  X,
  SpinnerGap,
  Lock,
  LockOpen
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

// Category icons and labels
const CATEGORIES = {
  contract: { icon: FileDoc, label: 'Contracts', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' },
  permit: { icon: Certificate, label: 'Permits', color: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' },
  insurance: { icon: Shield, label: 'Insurance', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300' },
  blueprint: { icon: Blueprint, label: 'Blueprints', color: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900 dark:text-cyan-300' },
  invoice: { icon: Receipt, label: 'Invoices', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300' },
  photo: { icon: Camera, label: 'Photos', color: 'bg-pink-100 text-pink-700 dark:bg-pink-900 dark:text-pink-300' },
  safety: { icon: WarningIcon, label: 'Safety Docs', color: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' },
  certification: { icon: CheckCircle, label: 'Certifications', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300' },
  other: { icon: FileText, label: 'Other', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' }
};

// File type icons
const getFileIcon = (contentType, filename) => {
  if (contentType?.includes('pdf')) return FilePdf;
  if (contentType?.includes('image')) return FileImage;
  if (contentType?.includes('spreadsheet') || filename?.match(/\.(xls|xlsx|csv)$/i)) return FileXls;
  if (contentType?.includes('zip') || contentType?.includes('rar')) return FileZip;
  if (contentType?.includes('word') || filename?.match(/\.(doc|docx)$/i)) return FileDoc;
  return FileText;
};

// Format file size
const formatSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const JobDocuments = ({ jobId, isContractor, isAssignedWorker }) => {
  const [documents, setDocuments] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [viewerUrl, setViewerUrl] = useState(null);
  
  // Upload form state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadCategory, setUploadCategory] = useState('other');
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadShared, setUploadShared] = useState(true);
  
  const fileInputRef = useRef(null);

  const canUpload = isContractor || isAssignedWorker;

  useEffect(() => {
    fetchDocuments();
    fetchStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  }, [jobId]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(
        `${API}/api/job-documents/job/${jobId}`,
        getAuthHeaders()
      );
      setDocuments(response.data.documents || []);
      setGrouped(response.data.grouped || {});
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(
        `${API}/api/job-documents/job/${jobId}/stats`,
        getAuthHeaders()
      );
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: 'File too large',
          description: 'Maximum file size is 10 MB',
          variant: 'destructive'
        });
        return;
      }
      setUploadFile(file);
      setUploadTitle(file.name);
      setShowUploadModal(true);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);

      const params = new URLSearchParams({
        category: uploadCategory,
        title: uploadTitle || uploadFile.name,
        description: uploadDescription,
        shared_with_workers: uploadShared.toString()
      });

      await axios.post(
        `${API}/api/job-documents/job/${jobId}/upload?${params}`,
        formData,
        {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast({
        title: 'Document uploaded',
        description: 'File has been uploaded successfully'
      });

      // Reset and refresh
      setShowUploadModal(false);
      setUploadFile(null);
      setUploadTitle('');
      setUploadDescription('');
      setUploadCategory('other');
      setUploadShared(true);
      fetchDocuments();
      fetchStats();
    } catch (error) {
      toast({
        title: 'Upload failed',
        description: error.response?.data?.detail || 'Failed to upload document',
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(
        `${API}/api/job-documents/document/${doc._id}/download`,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast({
        title: 'Download failed',
        description: 'Could not download the document',
        variant: 'destructive'
      });
    }
  };

  const handleView = async (doc) => {
    try {
      const response = await axios.get(
        `${API}/api/job-documents/document/${doc._id}/view`,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(response.data);
      setViewerUrl(url);
      setSelectedDoc(doc);
    } catch (error) {
      toast({
        title: 'View failed',
        description: 'Could not open the document',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}"? This cannot be undone.`)) return;

    try {
      await axios.delete(
        `${API}/api/job-documents/document/${doc._id}`,
        getAuthHeaders()
      );

      toast({
        title: 'Document deleted',
        description: 'The document has been removed'
      });

      fetchDocuments();
      fetchStats();
    } catch (error) {
      toast({
        title: 'Delete failed',
        description: error.response?.data?.detail || 'Could not delete document',
        variant: 'destructive'
      });
    }
  };

  const toggleSharing = async (doc) => {
    try {
      await axios.patch(
        `${API}/api/job-documents/document/${doc._id}`,
        { shared_with_workers: !doc.shared_with_workers },
        getAuthHeaders()
      );

      toast({
        title: doc.shared_with_workers ? 'Document hidden' : 'Document shared',
        description: doc.shared_with_workers 
          ? 'Workers can no longer see this document'
          : 'Workers can now view this document'
      });

      fetchDocuments();
    } catch (error) {
      toast({
        title: 'Update failed',
        variant: 'destructive'
      });
    }
  };

  const closeViewer = () => {
    if (viewerUrl) {
      window.URL.revokeObjectURL(viewerUrl);
    }
    setViewerUrl(null);
    setSelectedDoc(null);
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-neutral-900 border-2 border-black dark:border-neutral-700 rounded-xl p-6 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
        <div className="flex items-center justify-center py-8">
          <SpinnerGap size={32} className="animate-spin text-[#2B4C6F]" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-neutral-900 border-2 border-black dark:border-neutral-700 rounded-xl shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] overflow-hidden">
      {/* Header */}
      <div className="bg-[#2B4C6F] text-white p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FolderOpen size={28} weight="bold" />
          <div>
            <h3 className="font-bold text-lg">Job Documents</h3>
            {stats && (
              <p className="text-sm text-white/80">
                {stats.total_documents} files • {stats.total_size_formatted}
              </p>
            )}
          </div>
        </div>
        
        {canUpload && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileSelect}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp,.zip,.rar"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              className="bg-white text-[#2B4C6F] hover:bg-gray-100 border-2 border-black shadow-[2px_2px_0px_#000] active:translate-y-0.5 active:shadow-none font-bold"
              data-testid="upload-document-btn"
            >
              <Upload size={20} weight="bold" className="mr-2" />
              Upload
            </Button>
          </>
        )}
      </div>

      {/* Category Grid */}
      <div className="p-4">
        {documents.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <FolderOpen size={48} className="mx-auto mb-3 opacity-50" />
            <p className="font-medium">No documents yet</p>
            {canUpload && (
              <p className="text-sm mt-1">Upload contracts, permits, and other job files</p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {Object.entries(CATEGORIES).map(([key, config]) => {
              const categoryDocs = grouped[key] || [];
              if (categoryDocs.length === 0) return null;
              
              const CategoryIcon = config.icon;
              const isExpanded = expandedCategory === key;
              
              return (
                <div 
                  key={key}
                  className="border-2 border-black dark:border-neutral-700 rounded-lg overflow-hidden"
                >
                  {/* Category Header */}
                  <button
                    onClick={() => setExpandedCategory(isExpanded ? null : key)}
                    className={`w-full flex items-center justify-between p-3 ${config.color} hover:opacity-90 transition-opacity`}
                    data-testid={`category-${key}`}
                  >
                    <div className="flex items-center gap-3">
                      <CategoryIcon size={24} weight="bold" />
                      <span className="font-bold">{config.label}</span>
                      <Badge variant="secondary" className="bg-white/50 text-inherit">
                        {categoryDocs.length}
                      </Badge>
                    </div>
                    {isExpanded ? <CaretUp size={20} /> : <CaretDown size={20} />}
                  </button>
                  
                  {/* Document List */}
                  {isExpanded && (
                    <div className="bg-white dark:bg-neutral-800 divide-y divide-gray-200 dark:divide-neutral-700">
                      {categoryDocs.map((doc) => {
                        const FileIcon = getFileIcon(doc.content_type, doc.original_filename);
                        
                        return (
                          <div 
                            key={doc._id}
                            className="p-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-neutral-700/50"
                            data-testid={`document-${doc._id}`}
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <FileIcon size={24} className="text-gray-500 dark:text-gray-400 flex-shrink-0" />
                              <div className="min-w-0">
                                <p className="font-medium text-gray-900 dark:text-white truncate">
                                  {doc.title}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {formatSize(doc.size)} • {doc.uploaded_by_name} • {new Date(doc.created_at).toLocaleDateString()}
                                </p>
                              </div>
                              
                              {/* Privacy indicator for contractors */}
                              {isContractor && (
                                <Badge 
                                  variant="outline" 
                                  className={`flex-shrink-0 ${doc.shared_with_workers ? 'text-green-600 border-green-600' : 'text-amber-600 border-amber-600'}`}
                                >
                                  {doc.shared_with_workers ? (
                                    <><LockOpen size={12} className="mr-1" /> Shared</>
                                  ) : (
                                    <><Lock size={12} className="mr-1" /> Private</>
                                  )}
                                </Badge>
                              )}
                            </div>
                            
                            {/* Actions */}
                            <div className="flex items-center gap-1 ml-2">
                              {(doc.content_type?.includes('pdf') || doc.content_type?.includes('image')) && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleView(doc)}
                                  className="h-8 w-8 p-0"
                                  title="View"
                                >
                                  <Eye size={18} />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownload(doc)}
                                className="h-8 w-8 p-0"
                                title="Download"
                              >
                                <Download size={18} />
                              </Button>
                              {isContractor && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => toggleSharing(doc)}
                                    className="h-8 w-8 p-0"
                                    title={doc.shared_with_workers ? 'Hide from workers' : 'Share with workers'}
                                  >
                                    {doc.shared_with_workers ? <LockOpen size={18} /> : <Lock size={18} />}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDelete(doc)}
                                    className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                    title="Delete"
                                  >
                                    <Trash size={18} />
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div 
            className="bg-white dark:bg-neutral-900 rounded-xl border-2 border-black dark:border-neutral-700 shadow-[6px_6px_0px_#000] w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-[#2B4C6F] text-white p-4 rounded-t-lg flex items-center justify-between">
              <h3 className="font-bold text-lg">Upload Document</h3>
              <button onClick={() => setShowUploadModal(false)} className="hover:bg-white/20 p-1 rounded">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* File Info */}
              <div className="bg-gray-100 dark:bg-neutral-800 p-3 rounded-lg flex items-center gap-3">
                <FileText size={32} className="text-[#2B4C6F]" />
                <div className="min-w-0">
                  <p className="font-medium truncate">{uploadFile?.name}</p>
                  <p className="text-sm text-gray-500">{formatSize(uploadFile?.size)}</p>
                </div>
              </div>
              
              {/* Category */}
              <div>
                <Label className="font-bold">Category</Label>
                <select
                  value={uploadCategory}
                  onChange={(e) => setUploadCategory(e.target.value)}
                  className="w-full mt-1 p-2 border-2 border-black dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800"
                >
                  {Object.entries(CATEGORIES).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </select>
              </div>
              
              {/* Title */}
              <div>
                <Label className="font-bold">Title</Label>
                <Input
                  value={uploadTitle}
                  onChange={(e) => setUploadTitle(e.target.value)}
                  placeholder="Document title"
                  className="mt-1 border-2 border-black dark:border-neutral-700"
                />
              </div>
              
              {/* Description */}
              <div>
                <Label className="font-bold">Description (optional)</Label>
                <Textarea
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  placeholder="Add notes about this document..."
                  className="mt-1 border-2 border-black dark:border-neutral-700"
                  rows={2}
                />
              </div>
              
              {/* Sharing toggle (contractors only) */}
              {isContractor && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-neutral-800 rounded-lg">
                  <input
                    type="checkbox"
                    id="shareWithWorkers"
                    checked={uploadShared}
                    onChange={(e) => setUploadShared(e.target.checked)}
                    className="w-5 h-5 accent-[#2B4C6F]"
                  />
                  <label htmlFor="shareWithWorkers" className="flex-1">
                    <span className="font-medium">Share with assigned workers</span>
                    <p className="text-sm text-gray-500">Workers will be able to view and download this document</p>
                  </label>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowUploadModal(false)}
                  className="flex-1 border-2 border-black dark:border-neutral-700"
                  disabled={uploading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="flex-1 bg-[#2B4C6F] hover:bg-[#203A55] text-white border-2 border-black shadow-[2px_2px_0px_#000] active:translate-y-0.5 active:shadow-none font-bold"
                  data-testid="confirm-upload-btn"
                >
                  {uploading ? (
                    <><SpinnerGap size={20} className="animate-spin mr-2" /> Uploading...</>
                  ) : (
                    <><Upload size={20} className="mr-2" /> Upload</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Viewer Modal */}
      {viewerUrl && selectedDoc && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={closeViewer}
        >
          <div 
            className="bg-white dark:bg-neutral-900 rounded-xl border-2 border-black overflow-hidden max-w-4xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-[#2B4C6F] text-white p-3 flex items-center justify-between">
              <span className="font-bold truncate">{selectedDoc.title}</span>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDownload(selectedDoc)}
                  className="text-white hover:bg-white/20"
                >
                  <Download size={18} className="mr-1" /> Download
                </Button>
                <button onClick={closeViewer} className="hover:bg-white/20 p-1 rounded">
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-gray-100">
              {selectedDoc.content_type?.includes('pdf') ? (
                <iframe
                  src={viewerUrl}
                  className="w-full h-[75vh]"
                  title={selectedDoc.title}
                />
              ) : selectedDoc.content_type?.includes('image') ? (
                <img
                  src={viewerUrl}
                  alt={selectedDoc.title}
                  className="max-w-full max-h-[75vh] mx-auto"
                />
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDocuments;
